import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import {
  BodyText,
  Box,
  Card,
  DetailText,
  Divider,
  Flex,
  InlineLink,
  Link,
  Strong,
} from '@utilitywarehouse/hearth-react';
import {
  BasketMediumIcon,
  CashbackCardMediumIcon,
  CelebrationMediumIcon,
} from '@utilitywarehouse/hearth-react-icons';
import { compact } from 'lodash';
import { ReactNode, useEffect, useRef, useState } from 'react';

import { DialogID, useDialog } from '@components/Dialog';
import { getQuestionSlug } from '@components/FAQ/controlledAccordionState';
import { useFAQAccordionState } from '@components/FAQ/useFAQAccordionState';
import { NavBar } from '@components/NavBar';
import { UpdatingModal } from '@components/UpdatingModal';
import { DISCOUNT_IDS } from '@constants/apiConstants';
import { EXTERNAL_LINKS } from '@constants/externalLinks';
import { RemoveDialog } from '@features/RemoveDialog';
import { ProductType, Quote, RetailPartner } from '@generated/gql/graphql';
import { formatCurrency } from '@utils/format';
import { calculateDiscountedPrice, getQuoteMonthlyCharge } from '@utils/price';

import { Calculator } from './Calculator';
import { CashbackFAQ } from './CashbackFAQ';
import { Sections, Topics } from './CashbackFAQ/constants';
import { DontTakeOurWordForIt } from './DontTakeOurWordForIt';
import { EarningsMilestones } from './EarningsMilestones';
import { FeatureListItem } from './FeatureListItem';
import { RetailPartners } from './RetailPartners';
import './styles.scss';
import { useCashbackHandler } from './useCashbackHandler';

interface CashbackProps {
  quote: Quote;
  partners: RetailPartner[];
  coreServiceCount: number;
}

interface WrapperProps {
  children: ReactNode;
}

const earningsSection = Sections.earnings;
const cashbackEveryTimeTopic = Topics.cashbackEveryTime;
const cashbackEveryTimeSlug = getQuestionSlug(
  earningsSection,
  cashbackEveryTimeTopic
);

const Wrapper = ({ children }: WrapperProps) => (
  <Flex direction="column" spacing="2xl" className="wrapper cashback__Spaced">
    {children}
  </Flex>
);

const Price = ({ quote }: { quote: Quote }) => {
  const monthlyCharge = getQuoteMonthlyCharge(quote);
  const threeMonthsDiscount =
    quote.discounts.find(
      (discount) => discount.id === DISCOUNT_IDS.CBC_THREE_MONTHS_FREE_DISCOUNT
    ) ?? null;

  const renderPrice = () => {
    if (!monthlyCharge) {
      return null;
    }

    if (threeMonthsDiscount?.qualified) {
      const discountedPrice = calculateDiscountedPrice(
        monthlyCharge.amount,
        threeMonthsDiscount,
        true
      );

      return (
        <Box marginBottom="300">
          <BodyText
            data-testid="cashback-three-months-discount-price-label"
            size="sm"
            marginBottom="100"
          >
            <DetailText color="valid" size="2xl" marginRight="50" asChild>
              <span>{formatCurrency(discountedPrice)}</span>
            </DetailText>{' '}
            for three months, then{' '}
            <Strong>{formatCurrency(monthlyCharge.amount)} a month</Strong>
          </BodyText>
          <BodyText size="sm">
            No contract, cancel anytime.{' '}
            <InlineLink
              color="inherit"
              href={EXTERNAL_LINKS.termsCbcSection}
              rel="noreferrer noopener"
              target="_blank"
              hideOpenIcon
            >
              Terms Apply
            </InlineLink>
          </BodyText>
        </Box>
      );
    }

    return (
      <BodyText data-testid="cashback-price-label">
        <DetailText size="2xl" marginRight="50" asChild>
          <span>{formatCurrency(monthlyCharge.amount)}</span>
        </DetailText>{' '}
        a month
      </BodyText>
    );
  };

  return <div>{renderPrice()}</div>;
};

const Cashback = ({ quote, partners, coreServiceCount }: CashbackProps) => {
  const cbcCalcRef = useRef<Nullable<HTMLFormElement>>(null);
  const disableCommsRef = useRef<Nullable<() => void>>(null);
  const updatingDialog = useDialog(DialogID.UPDATING_BUNDLE);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);

  const {
    expandedState: faqTopicsExpanded,
    handleAccordionChange,
    expandFaqItem,
  } = useFAQAccordionState(cashbackEveryTimeSlug);

  const handleFAQEarnClick = () => {
    expandFaqItem(getQuestionSlug(Sections.earnings, Topics.monthlyLimit));
  };

  const {
    selected,
    product: { name },
    id,
  } = quote;

  const { selectCashback, removeCashback, isLoading, isRemoving } =
    useCashbackHandler({
      quoteId: id,
      isSelected: selected,
    });

  const handleSelectCashback = () => {
    selectCashback();
    if (cbcCalcRef.current) {
      cbcCalcRef.current.requestSubmit();
    }
  };

  const handleRemoveCashback = () => {
    disableCommsRef.current?.();
    removeCashback();
  };

  useEffect(() => {
    updatingDialog.setOpen(isLoading);
  }, [isLoading, updatingDialog]);

  return (
    <>
      <section className="cashback" data-testid="cashback-page-title">
        <header className="cashback__header wrapper">
          <Price quote={quote} />
        </header>
        <Flex direction="column">
          <Wrapper>
            <Flex
              className="cashback__features"
              spacing="xl"
              alignItems={{
                desktop: 'center',
                tablet: 'start',
                mobile: 'start',
              }}
              justifyContent="center"
              direction={{
                tablet: 'row',
                mobile: 'column',
              }}
            >
              <Card colorScheme="cashback" className="cashback__animation">
                <DotLottieReact
                  src="animations/cbc.json"
                  loop
                  autoplay
                  className="cashback__animation-container"
                />
              </Card>
              <ul className="cashback__list">
                <FeatureListItem
                  icon={<CashbackCardMediumIcon />}
                  title="Top up"
                >
                  Load money onto your Cashback Card from your debit card, using
                  the UW app or website.
                </FeatureListItem>
                <FeatureListItem
                  icon={<BasketMediumIcon />}
                  title="Go shopping"
                >
                  Spend money with your card online or in-store. You can make
                  this even easier by using Apple Pay or Google Pay.
                </FeatureListItem>
                <FeatureListItem
                  icon={<CelebrationMediumIcon />}
                  title="Lower your bill"
                >
                  Check the app to see your cashback appear instantly. At the
                  end of the month, we’ll automatically take your cashback
                  earnings off your UW bill.
                  <br />
                  <Link
                    href="#"
                    className="cashback__CalcLink"
                    onClick={(e) => {
                      e.preventDefault();
                      const elem = document.getElementById(
                        'cashback-calculator'
                      );
                      elem?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                      });
                    }}
                  >
                    How much could I earn?
                  </Link>
                </FeatureListItem>
              </ul>
            </Flex>
            <Divider />
          </Wrapper>

          <RetailPartners
            partners={compact(partners)}
            onFAQLinkClick={handleFAQEarnClick}
            Wrapper={Wrapper}
          />

          <Wrapper>
            <Calculator
              formRef={cbcCalcRef}
              disableCommsRef={disableCommsRef}
              coreServiceCount={coreServiceCount}
              onFAQLinkClick={handleFAQEarnClick}
            />
          </Wrapper>

          <EarningsMilestones />
          <DontTakeOurWordForIt />

          <CashbackFAQ
            faqTopicsExpanded={faqTopicsExpanded}
            onAccordionChange={handleAccordionChange}
          />
        </Flex>
      </section>
      <NavBar
        nextLabel={selected ? 'Save' : 'Add to bundle'}
        onNext={handleSelectCashback}
        nextDisabled={isLoading}
        hasRemoveButton={selected}
        onRemove={() => setRemoveDialogOpen(true)}
      />
      <RemoveDialog
        isOpen={removeDialogOpen}
        onClose={() => setRemoveDialogOpen(false)}
        onRemove={handleRemoveCashback}
        productType={ProductType.CashbackCard}
        productName={name}
      />
      <UpdatingModal
        heading="Updating your bundle..."
        productType={ProductType.CashbackCard}
        isRemoving={isRemoving}
      />
    </>
  );
};

export { Cashback };
